import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarClock, X } from "lucide-react";
import { DUE_PRESETS, listTodos, updateTodo, type Todo } from "@/lib/todos";
import { describeDue } from "@/lib/time";
import { useZone } from "@/components/session-provider";

/**
 * A follow-up that has come due, on whatever screen its author is looking at.
 *
 * ⚠️ **A CARD, NOT A MODAL.** Outlook and Google Calendar draw their reminders as a card in the
 * corner with Snooze and Dismiss, and never as a dialog that takes focus — because the person
 * is usually mid-sentence, and here that sentence is a reply to a requester that cannot be
 * taken back (D-149). This steals nothing: bottom-right, `role="status"`, no focus trap.
 *
 * ⚠️ **IT MAY SAY THE TITLE, and the reason is where it appears.** D-95 keeps the title and the
 * request out of the reminder EMAIL because mail leaves the tenant and cannot be recalled. This
 * is drawn behind a session, on the author's own screen, where the follow-ups page already
 * prints both — the same disclosure to the same one reader. Nothing here reaches a mailbox.
 *
 * ⚠️ **Due-ness is still a comparison against the clock** (D-59): the list is read when the
 * badge says something is due, and an item is due when its date has passed. Snooze writes a
 * new date through the ordinary edit endpoint, so the bell and email reminders re-arm the way
 * D-95 already handles. Dismiss is per device and per due date, in `localStorage` — a layout
 * choice in D-110's sense, never a change to the item, which stays due on the bell and the list.
 *
 * ⚠️ One card at a time. Several due at once fold into a count and a way to the list; a stack
 * of cards over somebody's screen is the interruption this exists to avoid.
 */

const DISMISSED_KEY = "ars-followup-reminders-dismissed";

/** Item id → the due date it was dismissed for. A rescheduled item comes back. */
type Dismissed = Record<string, string>;

function readDismissed(): Dismissed {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return typeof parsed === "object" && parsed !== null ? (parsed as Dismissed) : {};
  } catch {
    return {};
  }
}

function writeDismissed(next: Dismissed): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
  } catch {
    // Unwritable storage means the card may come back after a reload. It stays dismissable.
  }
}

const SNOOZES = [
  { key: "15m", label: "15 min", resolve: (now: Date) => new Date(now.getTime() + 15 * 60_000) },
  { key: "1h", label: "1 hour", resolve: (now: Date) => new Date(now.getTime() + 60 * 60_000) },
] as const;

export function FollowUpReminderCard({
  dueCount,
  onChanged,
}: {
  /** The shell's own badge count, so this fetches only when something is due. */
  dueCount: number;
  /** Called after a snooze, so the badge re-reads. */
  onChanged: () => void;
}) {
  const zone = useZone();
  const [due, setDue] = useState<Todo[]>([]);
  const [dismissed, setDismissed] = useState<Dismissed>(() => readDismissed());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (dueCount === 0) {
      setDue([]);
      return;
    }

    try {
      const list = await listTodos();
      const now = Date.now();

      // The server buckets by calendar day; "due" here is the clock having passed the date.
      setDue(
        [...list.overdue, ...list.today].filter(
          (t) => t.completedAtUtc === null && t.dueAtUtc !== null && Date.parse(t.dueAtUtc) <= now,
        ),
      );
    } catch {
      // A reminder that cannot load is one not drawn. Never an error over the screen in use.
      setDue([]);
    }
  }, [dueCount]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = due.filter((t) => dismissed[t.id] !== t.dueAtUtc);

  if (visible.length === 0) return null;

  function dismiss(items: Todo[]) {
    const next = { ...dismissed };
    for (const item of items) if (item.dueAtUtc) next[item.id] = item.dueAtUtc;
    writeDismissed(next);
    setDismissed(next);
  }

  async function snooze(item: Todo, until: Date | null) {
    if (!until) return;

    setBusy(true);

    try {
      await updateTodo(item.id, {
        title: item.title,
        notes: item.notes,
        dueAtUtc: until.toISOString(),
      });
      onChanged();
      await load();
    } catch {
      // Left on screen; the person can open the list and move it there.
    } finally {
      setBusy(false);
    }
  }

  const tomorrow = DUE_PRESETS.find((p) => p.key === "tomorrow");

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-20 right-4 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-3 shadow-lg md:bottom-4"
    >
      <div className="flex items-start gap-2">
        <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />

        <div className="min-w-0 flex-1">
          {visible.length === 1 ? (
            <>
              <p className="text-xs font-medium text-muted-foreground">
                Follow-up due · {describeDue(visible[0].dueAtUtc, zone)}
              </p>
              <p className="mt-0.5 break-words text-sm font-medium">{visible[0].title}</p>
              {visible[0].tickets.length > 0 && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {visible[0].tickets.map((t) => `#${t.displayNumber}`).join(" · ")}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm font-medium">{visible.length} follow-ups are due</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => dismiss(visible)}
          disabled={busy}
          aria-label="Dismiss"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Link
          to="/todos"
          onClick={() => dismiss(visible)}
          className="inline-flex h-8 items-center rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          Open
        </Link>

        {visible.length === 1 && (
          <>
            <span className="ml-1 text-[11px] text-muted-foreground">Snooze</span>
            {SNOOZES.map((s) => (
              <button
                key={s.key}
                type="button"
                disabled={busy}
                onClick={() => void snooze(visible[0], s.resolve(new Date()))}
                className="h-8 rounded-lg border border-input px-2 text-xs transition hover:bg-accent disabled:opacity-50"
              >
                {s.label}
              </button>
            ))}
            {tomorrow && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void snooze(visible[0], tomorrow.resolve(new Date(), zone))}
                className="h-8 rounded-lg border border-input px-2 text-xs transition hover:bg-accent disabled:opacity-50"
              >
                Tomorrow
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
